import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { moneySchema, planSellable, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import { cashdeskKeys } from './cashdeskKeys'

const planSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Nom tarjimalari; eski backendda maydon UMUMAN yo'q */
  nameI18n: z.record(z.string(), z.string()).nullish(),
  price: moneySchema,
  state: z.string(),
  /** Backend hisoblaydi: `state == ACTIVE && !misconfigured` */
  sellable: z.boolean().nullish(),
})

export interface CashPlan {
  id: string
  /** KANONIK nom — tarjima yo'q bo'lganda shu ko'rinadi */
  name: string
  nameI18n: Record<string, string>
  price: Money
  /**
   * Hozir sotish mumkinmi.
   *
   * ⚠️ `state === 'ACTIVE'` YETARLI EMAS: siyosati unutilgan paket ham
   * `ACTIVE` bo'lib turadi, lekin u bilan abonent ulana olmaydi. Qaror
   * backendniki (`Plan.isMisconfigured()`), bu yerda faqat o'qiladi.
   */
  sellable: boolean
}

const EMPTY_PLANS: readonly CashPlan[] = []
const EMPTY_PRICES: ReadonlyMap<string, Money> = new Map()
const EMPTY_BY_ID: ReadonlyMap<string, CashPlan> = new Map()

export interface CashPlanCatalog {
  /** FAQAT sotiladiganlari — paket berish ro'yxati shundan quriladi */
  sellable: readonly CashPlan[]
  /**
   * HAMMA paket, `id` bo'yicha — o'tmishdagi amalning nomini shundan
   * o'qiladi («Berilgan paketlar»).
   *
   * ⚠️ `sellable` DAN ALOHIDA va bu majburiy: kecha berilgan paket
   * bugun sotuvdan olingan bo'lishi mumkin, o'sha qatorning nomi esa
   * baribir ko'rsatilishi kerak. Sotuv ro'yxatidan qidirish tarixni
   * jimgina nomsiz qoldirardi.
   */
  byId: ReadonlyMap<string, CashPlan>
  /**
   * KANONIK nom → narx.
   *
   * ⚠️ Kalit nom, id EMAS: abonent katalogi (`subscriber.directory`)
   * `planId` bermaydi — u ADR-029 §3 dagi yopiq maydonlar ro'yxatiga
   * kirmagan. Nom esa keladi va u ham kanonik.
   *
   * ⚠️ `sellable` DAN ALOHIDA: abonentning joriy tarifi sotuvdan
   * olingan bo'lishi mumkin, narxi esa baribir kerak — tez to'ldirish
   * tugmalari shundan hisoblanadi.
   */
  priceByName: ReadonlyMap<string, Money>
}

/**
 * Paketlar katalogi.
 *
 * <h2>⚠️ NEGA ADMIN PANELIDAGI `shared/api/reference.ts` KO'CHIRILMADI</h2>
 * O'sha fayl o'n ikkita hook saqlaydi: faol sessiyalar, RADIUS realm,
 * siyosatlar, tugun ostidagi abonentlar soni. Sheriklar kabinetida
 * ularning birortasi ham ekran topmaydi, lekin nusxa ko'chirilsa
 * ularning kesh kalitlari, sxemalari va tarjima kalitlari ham birga
 * kelardi — ya'ni hech qachon chizilmaydigan kodni uch tilda tarjima
 * qilish kerak bo'lardi. Bu yerda faqat kerak bo'lgan bitta so'rov bor.
 *
 * ⚠️ Nom tarjimasi mantiqi TAKRORLANMAYDI — ekranda ko'rsatish payti
 * `shared/lib/planName` dan olinadi (`GrantPanel`) va admin bilan bir
 * xil zaxira zanjiriga ega: tarjima yo'q bo'lsa kanonik nom, ya'ni
 * ro'yxatda hech qachon bo'sh qator chiqmaydi.
 */
export function useCashPlans(): CashPlanCatalog {
  const query = useQuery({
    queryKey: cashdeskKeys.plans,
    queryFn: async (): Promise<CashPlan[]> => {
      const response = await api.get<unknown>('/api/catalog/plans')
      return z
        .array(planSchema)
        .parse(response.data)
        .map((raw) => ({
          id: raw.id,
          name: raw.name,
          nameI18n: raw.nameI18n ?? {},
          price: toMoney(raw.price),
          sellable: planSellable(raw),
        }))
    },
    // Katalog kamdan-kam o'zgaradi; kassir esa uni kuniga o'nlab marta ochadi
    staleTime: 5 * 60_000,
  })

  const plans = query.data

  return useMemo(() => {
    if (!plans || plans.length === 0) {
      return { sellable: EMPTY_PLANS, byId: EMPTY_BY_ID, priceByName: EMPTY_PRICES }
    }
    return {
      sellable: plans.filter((p) => p.sellable),
      byId: new Map(plans.map((p) => [p.id, p])),
      priceByName: new Map(plans.map((p) => [p.name, p.price])),
    }
  }, [plans])
}
